import { useState, useEffect } from "react";
import { apiGet } from "../api/client";
import { PageHeader } from "../components/ui/Primitives";
import { ScrollText, RefreshCw, Filter, Terminal } from "lucide-react";

export default function Logs() {
  const [logs, setLogs] = useState<any[]>([]);
  const [level, setLevel] = useState("");
  const [process, setProcess] = useState("");
  const [lines, setLines] = useState(100);
  const [loading, setLoading] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ lines: String(lines) });
      if (level) params.set("level", level);
      if (process) params.set("process", process);
      const data = await apiGet<any>(`/logs/recent?${params}`);
      setLogs(data.logs || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 5000);
    return () => clearInterval(interval);
  }, [level, process, lines]);

  return (
    <div>
      <PageHeader
        icon={<ScrollText size={24} color="var(--accent)" />}
        title="시스템 로그"
        description="전체 프로세스 구조화 JSON 로그 실시간 스트림 (5초 자동 갱신)"
        actions={
          <button className="btn-secondary" onClick={fetchLogs} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : ""} /> 새로고침
          </button>
        }
      />

      {/* Filters Toolbar */}
      <div className="card" style={{ marginBottom: "var(--space-4)", padding: "var(--space-3) var(--space-4)" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-muted)", fontSize: "var(--text-sm)", fontWeight: 700 }}>
            <Filter size={14} /> 필터:
          </div>

          <label>
            로그 등급:
            <select value={level} onChange={(e) => setLevel(e.target.value)}>
              <option value="">전체 등급 (All)</option>
              <option value="DEBUG">DEBUG (디버그)</option>
              <option value="INFO">INFO (정보)</option>
              <option value="WARNING">WARNING (경고)</option>
              <option value="ERROR">ERROR (에러)</option>
              <option value="CRITICAL">CRITICAL (치명적)</option>
            </select>
          </label>

          <label>
            프로세스:
            <select value={process} onChange={(e) => setProcess(e.target.value)}>
              <option value="">전체 프로세스</option>
              <option value="ingest">수집 (ingest)</option>
              <option value="brain">뇌/피처 (brain)</option>
              <option value="infer">추론 (infer)</option>
              <option value="exec">실행 (exec)</option>
              <option value="api">관제탑 (api)</option>
              <option value="sandbox">샌드박스 (sandbox)</option>
            </select>
          </label>

          <label>
            출력 줄 수:
            <select value={lines} onChange={(e) => setLines(+e.target.value)}>
              <option value={50}>50줄</option>
              <option value={100}>100줄</option>
              <option value={500}>500줄</option>
              <option value={1000}>1000줄</option>
            </select>
          </label>
        </div>
      </div>

      {/* Log Terminal Window */}
      <div className="card" style={{ padding: "var(--space-3)" }}>
        <div className="card-title-row" style={{ padding: "4px 8px", marginBottom: "var(--space-2)" }}>
          <Terminal size={16} color="var(--accent)" />
          <h3 className="card-title" style={{ fontSize: 13 }}>로그 뷰어 콘솔</h3>
          <span className="card-title-meta" style={{ fontSize: "var(--text-xs)" }}>
            조회 결과: {logs.length}줄
          </span>
        </div>

        {loading && logs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-muted)" }}>
            로그 스트림 수신 중...
          </div>
        ) : logs.length === 0 ? (
          <div style={{ color: "var(--text-muted)", padding: "40px 20px", textAlign: "center", fontStyle: "italic" }}>
            조회된 로그가 없습니다
          </div>
        ) : (
          <div
            style={{
              maxHeight: "72vh",
              overflowY: "auto",
              fontFamily: "var(--font-mono)",
              fontSize: "var(--text-sm)",
              lineHeight: 1.6,
              background: "var(--bg-primary)",
              padding: "12px 14px",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border)",
            }}
          >
            {logs.map((log, i) => {
              const logLevel = (log.level || "INFO").toUpperCase();
              const color =
                logLevel === "ERROR" || logLevel === "CRITICAL"
                  ? "var(--red)"
                  : logLevel === "WARNING"
                  ? "var(--yellow)"
                  : logLevel === "DEBUG"
                  ? "var(--text-muted)"
                  : "var(--text-primary)";
              return (
                <div
                  key={i}
                  style={{
                    padding: "3px 0",
                    borderBottom: "1px solid rgba(255,255,255,0.03)",
                    color,
                    wordBreak: "break-all",
                  }}
                >
                  <span style={{ color: "var(--text-muted)", marginRight: 8, fontSize: "var(--text-xs)" }}>
                    {log.timestamp || log.ts || ""}
                  </span>
                  <span style={{ fontWeight: 800, marginRight: 6 }}>[{logLevel}]</span>
                  <span style={{ color: "var(--accent)", fontWeight: 600, marginRight: 8 }}>
                    {log.event || log.logger || log.process || ""}
                  </span>
                  <span style={{ color: "var(--text-primary)" }}>
                    {log.message || JSON.stringify(log)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
